import { useCallback, useMemo, useState } from 'react'

import type { Row } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'

import EditIcon from '@/assets/icons/icon-edit.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { Tooltip } from '@/components/ui/tooltip'
import type { TeamRole } from '@/modules/team/types'
import { AppRoutes } from '@/utils/constants'
import { classNames } from '@/utils/helpers'

import { TeamRoleRemoveDialog } from './team-role-remove-dialog'

export interface TeamRoleActionsProps {
  row: Row<TeamRole>
}

export const TeamRoleActions = ({ row }: TeamRoleActionsProps) => {
  const [showRemoveDialog, setRemoveEditDialog] = useState(false)

  const redirect = useNavigate()

  const openRemoveDialog = () => {
    setRemoveEditDialog(true)
  }

  const redirectToEditPage = useCallback(() => {
    redirect(AppRoutes.Teams.Roles.Edit.asUrl(String(row.original.id)))
  }, [redirect, row.original.id])

  const renderActionItems = useMemo(() => {
    return (
      <div className="float-right flex gap-4">
        <button type="button">
          <EditIcon
            width={18}
            height={18}
            className="text-primary-500 hover:text-primary-800"
            onClick={redirectToEditPage}
          >
            Edit
          </EditIcon>
        </button>

        <Tooltip
          content={row.original.isDefault && "Default roles can't be deleted"}
          asChild
        >
          <button
            type="button"
            disabled={row.original.isDefault}
            className={classNames(
              row.original.isDefault && 'opacity-50',
              'cursor-pointer',
            )}
          >
            <RemoveIcon
              width={18}
              height={18}
              onClick={openRemoveDialog}
              className={classNames(
                'text-danger-default',
                !row.original.isDefault && 'hover:text-danger-dark',
              )}
            >
              Remove
            </RemoveIcon>
          </button>
        </Tooltip>
      </div>
    )
  }, [redirectToEditPage, row.original.isDefault])

  return (
    <>
      {renderActionItems}
      <TeamRoleRemoveDialog
        row={row}
        setRemoveEditDialog={setRemoveEditDialog}
        showRemoveDialog={showRemoveDialog}
      />
    </>
  )
}
